import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";

/* -------------------------------------------------------------
   Sobre Arteconciencia — qué es, misión e impacto social.
   Contenido basado únicamente en textos ya existentes del sitio.
   ------------------------------------------------------------- */
export default function SobreArteconcienciasv() {
  const { lang } = useLang();
  const isEn = lang === "en";

  useSEO({
    title: "Sobre Arteconciencia | Arte y concientización en El Salvador",
    description:
      "Conoce Arteconciencia, una iniciativa de arte contemporáneo en El Salvador que utiliza la creación artística como herramienta de concientización, diálogo e impacto social.",
    canonical: "/sobre-arteconcienciasv",
  });

  const sections = [
    {
      n: "01",
      title: isEn ? "What is Arteconciencia" : "Qué es Arteconciencia",
      body: isEn
        ? "Arteconciencia is a contemporary art platform in El Salvador that brings together art, culture and awareness through exhibitions, artists and projects with social impact."
        : "Arteconciencia es una plataforma de arte contemporáneo en El Salvador que une arte, cultura y concientización a través de exposiciones, artistas y proyectos con impacto social.",
    },
    {
      n: "02",
      title: isEn ? "Mission" : "Misión",
      body: isEn
        ? "To transform art into an act of awareness, encounter and impact — inviting us to pause, to feel and to remember the immense value of being alive."
        : "Transformar el arte en un acto de conciencia, encuentro e impacto — invitar a detenernos, sentir y recordar el inmenso valor de estar vivos.",
    },
    {
      n: "03",
      title: isEn ? "Awareness and social impact" : "Concientización e impacto social",
      body: isEn
        ? "LATIR — El placer de vivir benefits Fundarosa, an organization that accompanies women in the prevention, early detection and treatment of breast cancer. Every artwork acquired contributes directly to making that work possible."
        : "LATIR — El placer de vivir es a beneficio de Fundarosa, organización que acompaña a mujeres en la prevención, detección temprana y tratamiento del cáncer de mama. Cada obra adquirida contribuye directamente a hacer posible esa labor.",
    },
    {
      n: "04",
      title: isEn ? "Exhibitions and projects" : "Exposiciones y proyectos",
      body: isEn
        ? "LATIR — El placer de vivir (2026) is Arteconciencia's exhibition at the Palacio Nacional de El Salvador, in San Salvador, bringing together more than 50 national and international artists around art and awareness. New projects are currently in preparation."
        : "LATIR — El placer de vivir (2026) es la exposición de Arteconciencia en el Palacio Nacional de El Salvador, en San Salvador, y reúne a más de 50 artistas nacionales e internacionales en torno al arte y la concientización. Nuevos proyectos se encuentran en preparación.",
    },
  ];

  return (
    <main className="bg-paper min-h-screen" data-testid="sobre-arteconcienciasv-page">
      <section className="pt-28 sm:pt-32 md:pt-36 pb-14 md:pb-20">
        <div className="mx-auto max-w-[1180px] px-5 md:px-10">
          <span className="eyebrow text-ink-mute inline-flex items-center gap-3 mb-6">
            <span className="w-6 h-px bg-latir-red inline-block" />
            {isEn ? "The platform" : "La plataforma"}
          </span>
          <h1
            className="font-display text-[2.1rem] sm:text-[3rem] lg:text-[3.6rem] leading-[1.05] tracking-[-0.01em] text-ink max-w-[20ch]"
            data-testid="sobre-arteconcienciasv-title"
          >
            {isEn ? "About Arteconciencia" : "Sobre Arteconciencia"}
          </h1>
          <p className="mt-6 font-editorial text-[1.05rem] sm:text-[1.25rem] leading-[1.5] text-ink-soft max-w-[52ch]">
            {isEn
              ? "Contemporary art in El Salvador, at the service of awareness and social impact."
              : "Arte contemporáneo en El Salvador, al servicio de la concientización y el impacto social."}
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="mx-auto max-w-[1180px] px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
            {sections.map((s) => (
              <article key={s.n} className="border-t border-line pt-5" data-testid={`sobre-ac-section-${s.n}`}>
                <div className="flex items-baseline gap-4 mb-3">
                  <span className="font-ui text-[0.62rem] tracking-[0.22em] text-latir-red">{s.n}</span>
                  <h2 className="font-display text-[1.3rem] sm:text-[1.5rem] leading-[1.15] text-ink">{s.title}</h2>
                </div>
                <p className="font-editorial text-[0.95rem] sm:text-[1rem] leading-[1.65] text-ink-soft max-w-[58ch]">
                  {s.body}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-16 md:mt-20 border-t border-line pt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/latir"
              data-testid="sobre-ac-cta-latir"
              className="inline-flex items-center gap-2 font-ui uppercase tracking-[0.18em] text-[0.7rem] border border-ink px-5 py-3 hover:bg-ink hover:text-paper transition-colors"
            >
              {isEn ? "Explore LATIR — El placer de vivir" : "Recorrer LATIR — El placer de vivir"}
            </Link>
            <Link
              to="/artistas"
              data-testid="sobre-ac-cta-artistas"
              className="inline-flex items-center gap-2 font-ui uppercase tracking-[0.18em] text-[0.7rem] text-ink-soft hover:text-latir-red transition-colors"
            >
              {isEn ? "Meet the artists" : "Conocer a los artistas"} →
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
